import { useEffect, useState } from "react";
import {
    Container,
    Box,
    Typography,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import { useNav } from "../../context/NavProvider";
import { useClient } from "../../context/ClientProvider";
import { useAccessibility } from "../../context/AccessibilityProvider";
import { useAuth } from "../../context/AuthProvider";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallMeDialog from "../pages/CallMeDialog";
import ScrollToTop from '../nav/ScrollToTop';
import { useUtils } from "../../context/UtilsProvider";
import { useNavigate } from "react-router-dom";
import ActionButton from "../account/ActionButton";
import { useAnalytics } from "../../context/AnalyticsProvider";

const Accessibility = () => {
    const { formState } = useUtils();
    const { useGetConfig } = useClient();
    const { clientConfig } = useGetConfig();
    const { postAccessibility, postNote } = useAccessibility();
    const { accessToken } = useAuth();
    const [consentIsChecked, setConsentIsChecked] = useState(false);
    const [middleCheckboxesEnabled, setMiddleCheckboxesEnabled] = useState(false);
    const [callbackCheckboxesEnabled, setCallbackCheckboxesEnabled] = useState(false);
    const [notApplicableChecked, setNotApplicableChecked] = useState(false);
    const [ukRelayChecked, setUkRelayChecked] = useState(false);
    const [translatorChecked, setTranslatorChecked] = useState(false);
    const [additionalNeeds, setAdditionalNeeds] = useState(false);
    const [mentalHealthChecked, setMentalHealthChecked] = useState(false);
    const [physicalNeedsChecked, setPhysicalNeedsChecked] = useState(false);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { setShowFooter } = useNav();
    const [openCallback, setOpenCallback] = useState(false);
    const { setTag } = useAnalytics();
    const navigate = useNavigate();

    setShowFooter(false);

    useEffect(() => {
        setMiddleCheckboxesEnabled(consentIsChecked);
    }, [consentIsChecked]);

    useEffect(() => {
        const hasMiddleSelection = notApplicableChecked || ukRelayChecked || translatorChecked;
        setSaveButtonEnabled(consentIsChecked && hasMiddleSelection);
        setCallbackCheckboxesEnabled(consentIsChecked && hasMiddleSelection);
    }, [consentIsChecked, notApplicableChecked, ukRelayChecked, translatorChecked]);

    const handleSave = async () => {
        if (!consentIsChecked || !saveButtonEnabled) return;

        try {
        const payload = {
            NotApplicable: notApplicableChecked,
            UKRelayService: ukRelayChecked,
            Translator: translatorChecked,
            AudioFiles: false,
            LargePrint: false,
            MiscOption1: false,
            MiscOption2: false,
            MiscOption3: false
        };

            await postAccessibility(payload);
            await postNote({ Note: "CUSTOMER ACCESSIBILITY OPTIONS HAVE BEEN UPDATED" });

            try {
                let note = null;
                const tags = [];

                if (notApplicableChecked) {
                    note = "Customer has indicated that accessibility options are not applicable";
                    tags.push("not_applicable_acc");
                } else if (ukRelayChecked && translatorChecked) {
                    note = "Customer has indicated that they use the UK Relay Service and a translator when communicating.";
                    tags.push("uk_relay_acc", "translator_acc");
                } else if (ukRelayChecked) {
                    note = "Customer has indicated that they use UK Relay Service when communicating on the phone.";
                    tags.push("uk_relay_acc");
                } else if (translatorChecked) {
                    note = "Customer has indicated that they speak through a translator or appointed 3rd party so will have someone with them if they contact us.";
                    tags.push("translator_acc");
                }

                if (note) {
                    await postNote({ Note: note });
                    for (const t of tags) {
                        await setTag(t);
                    }
                    await setTag("accessibility_complete");
                }
            } catch (err) {
                console.error("Analytics tag failed:", err);
            }

        try {
        // Post any additional support notes (no analytics)
            if (additionalNeeds) {
                await postNote({ Note: "CUSTOMER ALERT: This customer has expressed that they have additional accessibility needs that they would like to discuss with us." });
            }
            if (mentalHealthChecked) {
                await postNote({ Note: "CUSTOMER ALERT: This customer has expressed that they have Mental Health needs that impacts them financially that they would like to discuss." });
            }
            if (physicalNeedsChecked) {
                await postNote({ Note: "CUSTOMER ALERT: This customer has expressed that they have Physical Health needs that impacts them financially that they would like to discuss." });
            }
            } catch (err) {
                console.error("Failed to post additional notes:", err);
            }

            localStorage.setItem("accessibilitySubmitted", true);
            setIsSubmitted(true);
        } catch (error) {
            console.error("Save error:", error);
        }
    };

    const handleReturnHome = () => {
        navigate("/account");
    };

    const handleCloseCallback = () => {
        setOpenCallback(false);
        navigate("/account");
    };

    const handleOpenCallback = () => {
        setOpenCallback(true);
    };

    return (
        clientConfig && (
            <>
                <Container sx={{ mt: 4, paddingBottom: "750px" }}>
                    <ScrollToTop />
                    {!isSubmitted && (
                        <>
                            <Box>
                                <Typography fontSize={24} fontWeight="bold" paddingTop={1} paddingBottom={3}>Accessibility</Typography>
                                <Typography type="body1">At {clientConfig?.name}, our customers are at the heart of everything that we do. To help ensure we can support appropriately and meet your needs there are a few questions we'd like to ask you.</Typography>
                                <Typography type="body1" sx={{ fontWeight: "bold", mb: 1, mt: 3 }}>We will store this information alongside your other account details, so that we can provide you with the appropriate support you need.</Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={consentIsChecked}
                                            onChange={() => {
                                                setConsentIsChecked(!consentIsChecked);
                                                if (!consentIsChecked) {
                                                    setNotApplicableChecked(false);
                                                    setUkRelayChecked(false);
                                                    setTranslatorChecked(false);
                                                    setAdditionalNeeds(false);
                                                    setMentalHealthChecked(false);
                                                    setPhysicalNeedsChecked(false);
                                                    setSaveButtonEnabled(false);
                                                }
                                            }}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{ marginRight: { xs: "8px", sm: 0 }, marginBottom: { xs: "8px", sm: 0 } }}
                                        />
                                    }
                                    label="By selecting this box, you are giving us consent to do this."
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                            </Box>
                            <Box>
                                <Typography sx={{ mb: 1, mt: 3, fontWeight: "bold" }}>When we communicate with you, we want to be able to do this in a way that best fits your circumstances and needs. Are any of the following statements relevant to you and your personal circumstances? Please select from the below:</Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={notApplicableChecked}
                                            onChange={() => setNotApplicableChecked(!notApplicableChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{ marginRight: { xs: "8px", sm: 0 }, marginBottom: { xs: "8px", sm: 0 } }}
                                            disabled={!middleCheckboxesEnabled || ukRelayChecked || translatorChecked}
                                        />
                                    }
                                    label="Not applicable"
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={ukRelayChecked}
                                            onChange={() => setUkRelayChecked(!ukRelayChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{ marginRight: { xs: "8px", sm: 0 }, marginBottom: { xs: "8px", sm: 0 } }}
                                            disabled={!middleCheckboxesEnabled || notApplicableChecked}
                                        />
                                    }
                                    label="I use the UK Relay Service when communicating on the phone"
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={translatorChecked}
                                            onChange={() => setTranslatorChecked(!translatorChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{ marginRight: { xs: "8px", sm: 0 }, marginBottom: { xs: "8px", sm: 0 } }}
                                            disabled={!middleCheckboxesEnabled || notApplicableChecked}
                                        />
                                    }
                                    label="I usually speak through a translator or appointed 3rd party so I will have someone with me if I contact you"
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                            </Box>
                            <Box>
                                <Typography sx={{ mb: 1, mt: 3, fontWeight: "bold" }}>Additional Support</Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={additionalNeeds}
                                            onChange={() => setAdditionalNeeds(!additionalNeeds)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{ marginRight: { xs: "8px", sm: 0 }, marginBottom: { xs: "8px", sm: 0 } }}
                                            disabled={!callbackCheckboxesEnabled || mentalHealthChecked || physicalNeedsChecked}
                                        />
                                    }
                                    label={<>I have <strong>additional accessibility needs</strong> that don't appear on the list above and would require a callback to discuss them.</>}
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mentalHealthChecked}
                                            onChange={() => setMentalHealthChecked(!mentalHealthChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{ marginRight: { xs: "8px", sm: 0 }, marginBottom: { xs: "8px", sm: 0 } }}
                                            disabled={!callbackCheckboxesEnabled || additionalNeeds || physicalNeedsChecked}
                                        />
                                    }
                                    label={<>I have <strong>mental health needs</strong> that are impacting me financially and would like to discuss them.</>}
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={physicalNeedsChecked}
                                            onChange={() => setPhysicalNeedsChecked(!physicalNeedsChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{ marginRight: { xs: "8px", sm: 0 }, marginBottom: { xs: "8px", sm: 0 } }}
                                            disabled={!callbackCheckboxesEnabled || additionalNeeds || mentalHealthChecked}
                                        />
                                    }
                                    label={<>I have <strong>physical health needs</strong> that are impacting me financially and would like a callback to discuss them.</>}
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                                <Typography type="body1" sx={{ fontStyle: "italic", mb: 1, mt: 1, fontSize: "14px" }}>Once you click Save my preferences with one of the additional support options, you will be redirected to schedule your callback request</Typography>
                            </Box>
                            <ActionButton
                                type="submit"
                                variant="contained"
                                buttonColor="secondary.main"
                                buttonTextColor="primary.contrastText"
                                borderColor="transparent"
                                hoverColor="primary.contrastText"
                                hoverBorderColor="secondary.main"
                                hoverTextColor="secondary.main"
                                buttonTitle="Save my preferences"
                                disabled={!saveButtonEnabled}
                                onClick={handleSave}
                            />
                        </>
                    )}{isSubmitted && (
                        <Box sx={{ textAlign: "center", mt: 8 }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
                                <Typography variant="h5" sx={{ mt: 2, mb: 2, fontWeight: "bold" }}>Thank you for submitting your accessibility preferences!</Typography>
                            <Typography sx={{ mt: 2, mb: 2 }}>
                                    We've received your details and will use them to make sure you get the support you need. If your circumstances change, you can update your preferences at any time through your account.
                                {!clientConfig?.layout.callMe.active &&
                                    middleCheckboxesEnabled &&
                                        (additionalNeeds || mentalHealthChecked || physicalNeedsChecked) && (
                                        <Typography sx={{ mt: 2 }}>
                                            * Unfortunately our call back service is currently unavailable. Please contact us on
                                            <Typography sx={{ ml: 1 }}>
                                                <a href={`tel:${clientConfig?.layout?.arrearsCard?.tel}`} style={{ textDecoration: "underline" }}>
                                                    {clientConfig?.layout?.arrearsCard?.tel}
                                                </a>
                                            </Typography>
                                        </Typography>
                                        )}
                            </Typography>
                            {!clientConfig?.layout.callMe.active &&
                                middleCheckboxesEnabled &&
                                    (additionalNeeds || mentalHealthChecked || physicalNeedsChecked) && (
                                    <ActionButton
                                        variant="contained"
                                        buttonColor="secondary.main"
                                        buttonTextColor="primary.contrastText"
                                        borderColor="transparent"
                                        hoverColor="primary.contrastText"
                                        hoverBorderColor="secondary.main"
                                        hoverTextColor="secondary.main"
                                        buttonTitle="Return Home"
                                        onClick={handleReturnHome}
                                    />
                                    )}
                            {clientConfig?.layout.callMe.active &&
                                middleCheckboxesEnabled &&
                                    !(additionalNeeds || mentalHealthChecked || physicalNeedsChecked) && (
                                    <ActionButton
                                        variant="contained"
                                        buttonColor="secondary.main"
                                        buttonTextColor="primary.contrastText"
                                        borderColor="transparent"
                                        hoverColor="primary.contrastText"
                                        hoverBorderColor="secondary.main"
                                        hoverTextColor="secondary.main"
                                        buttonTitle="Return Home"
                                        onClick={handleReturnHome}
                                        />
                                    )}
                                {clientConfig?.layout.callMe.active &&
                                    (additionalNeeds || mentalHealthChecked || physicalNeedsChecked) && (
                                    <ActionButton
                                        variant="contained"
                                        buttonColor="secondary.main"
                                        buttonTextColor="primary.contrastText"
                                        borderColor="transparent"
                                        hoverColor="primary.contrastText"
                                        hoverBorderColor="secondary.main"
                                        hoverTextColor="secondary.main"
                                        buttonTitle="Set a call back"
                                        onClick={handleOpenCallback}
                                    />
                                    )}
                        </Box>
                    )}

                    {accessToken && clientConfig?.layout.callMe.active && (
                        <CallMeDialog open={openCallback} onClose={handleCloseCallback} dateOfBirth={formState?.dateOfBirth} />
                    )}
                </Container>
                {setShowFooter(true)}
            </>
        )
    );
};

export default Accessibility;
